import AppLayout from "@/components/layout/AppLayout";
import ProjectSummary from "@/components/ProjectSummary";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getProjectMonthlyBreakdownService } from "@/service/project.service";
import {
  getProjectTrackingDetail,
  selectSelectedProject,
} from "@/store/slices/projectTrackingSlice";
import { ProjectMonthlyAnalytics } from "@/types/project.model";
import { capitalizeFirstLetter } from "@/utils/constants";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

function MonthBreakdown() {
  const [monthlyBreakdown, setMonthlyBreakdown] =
    useState<ProjectMonthlyAnalytics | null>(null);
  const [loading, setLoading] = useState(false);

  // Use ref to track if we've already made the API call to prevent duplicate calls
  const hasCalledMonthlyBreakdown = useRef(false);

  const { projectId } = useParams();
  const projectDetail = useAppSelector(selectSelectedProject);

  const dispatch = useAppDispatch();

  useEffect(() => {
    // Only fetch project details if not already available
    // This avoids redundant API calls since ProjectTrackingReport already fetches it
    if (projectId && !projectDetail) {
      dispatch(getProjectTrackingDetail(projectId));
    }
  }, [projectId, projectDetail, dispatch]);

  useEffect(() => {
    // Separate effect for monthly breakdown data to avoid dependency issues
    if (projectId && !hasCalledMonthlyBreakdown.current) {
      // Set the ref to true to prevent duplicate calls
      hasCalledMonthlyBreakdown.current = true;

      setLoading(true);

      getProjectMonthlyBreakdownService(projectId)
        .then((response) => {
          setMonthlyBreakdown(response.data.data);
        })
        .catch((err) => {
          console.error("Error fetching monthly breakdown:", err);
          // errorToast("Error", "Failed to load monthly breakdown data");
          // Reset the ref on error so we can try again if needed
          hasCalledMonthlyBreakdown.current = false;
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [projectId]);

  return (
    <AppLayout
      title="Month wise breakdown"
      showBreadcrumb={true}
      contentClassName={`min-h-[90%]`}
      rightActions={<></>}
    >
      <div className="bg-white rounded-lg p-3 ">
        <ProjectSummary
          projectName={capitalizeFirstLetter(projectDetail?.name || "")}
          projectId={projectId} // Pass projectId to ProjectSummary
          startDate={projectDetail?.start_date || ""}
          endDate={projectDetail?.end_date || ""}
          hoursEstimated={
            projectDetail?.tracker?.total_esti_hrs?.toString() || "0"
          }
          status={capitalizeFirstLetter(projectDetail?.status || "")}
          hoursUtilized={projectDetail?.util_hrs?.toString() || "0"}
          remainingHours={projectDetail?.remi_hrs?.toString() || "0"}
        />

        {/* Monthly breakdown */}
        <div
          className="mt-5 mb-8 bg-white rounded-lg px-5 py-6"
          style={{
            boxShadow: "0px 10px 60px 0px #E2ECF980",
          }}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E0E0E0] border border-t-0 border-r-0 border-[#E0E0E0]">
              <thead className="bg-[#F4F7F9]">
                <tr>
                  <th className="px-4 py-3 text-left text-[14px] font-semibold text-[#1E1E1E]">
                    Month
                  </th>
                  <th className="px-4 py-3 text-left text-[14px] font-semibold text-[#1E1E1E]">
                    Hours
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E0E0E0]">
                {!loading ? (
                  <>
                    {monthlyBreakdown?.monthly_analytics &&
                    monthlyBreakdown.monthly_analytics.length > 0 ? (
                      monthlyBreakdown.monthly_analytics.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium">
                            {item.month}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium">
                            {item.utilized_hours}
                          </td>
                          {/* <td className="px-4 py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium">
                          {item.utilized_hours}hrs
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium">
                          {item.remaining_hours}hrs
                        </td> */}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={2}
                          className="px-4 py-3 text-center text-[14px] text-[#1E1E1E]"
                        >
                          No monthly data available
                        </td>
                      </tr>
                    )}
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-4 py-3 text-center text-[14px] text-[#1E1E1E]"
                    >
                      Loading...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default MonthBreakdown;
